import { z } from 'zod';
import { DEVICE_CHECK_RESULTS, DEVICE_TYPES } from '../types';
import { sumDeviceTypes } from '../utils';

export type TimeWindow = { start: Date; end: Date };

export const WORK_WINDOWS = [
  { start: [8, 0], end: [12, 0] },
  { start: [12, 30], end: [15, 30] },
] as const;

export const MIN_ADVANCE_MS = 24 * 60 * 60_000;
export const MAX_ADVANCE_MS = 6 * 30 * 24 * 60 * 60_000;
export const DEVICE_SLOT_MS = 10 * 60_000;

export const createAppointmentSchema = z
  .object({
    locationId: z.number().int().positive(),
    startDateTime: z
      .string()
      .transform((s) => new Date(s))
      .pipe(
        z
          .date()
          .refine((d) => d.getDay() !== 0 && d.getDay() !== 6, 'Cannot book on weekends')
          .refine(
            (d) => d.getTime() >= Date.now() + MIN_ADVANCE_MS,
            'Must be at least 24 h in the future',
          )
          .refine(
            (d) => d.getTime() <= Date.now() + MAX_ADVANCE_MS,
            'Cannot be more than 6 months in the future',
          ),
      ),
    deviceTypes: z
      .array(z.object({ type: z.enum(DEVICE_TYPES), count: z.number().int().positive() }))
      .min(1),
  })
  .superRefine(({ startDateTime, deviceTypes }, ctx) => {
    const totalCount = sumDeviceTypes(deviceTypes);
    const endDateTime = new Date(startDateTime.getTime() + totalCount * DEVICE_SLOT_MS);
    const y = startDateTime.getFullYear();
    const mo = startDateTime.getMonth();
    const d = startDateTime.getDate();
    const fitsWindow = WORK_WINDOWS.some(({ start, end }) => {
      const wStart = new Date(y, mo, d, start[0], start[1]);
      const wEnd = new Date(y, mo, d, end[0], end[1]);
      return startDateTime >= wStart && endDateTime <= wEnd;
    });
    if (!fitsWindow) {
      ctx.addIssue({
        code: 'custom',
        path: ['startDateTime'],
        message: 'Appointment must fall within business hours including its duration',
      });
    }
  });

export type CreateAppointmentInput = z.infer<typeof createAppointmentSchema>;

const deviceCheckItemSchema = z
  .object({
    name: z.string().min(1).max(255),
    type: z.enum(DEVICE_TYPES),
    result: z.enum(DEVICE_CHECK_RESULTS),
    comment: z.string().max(2000).optional(),
  })
  .superRefine(({ result, comment }, ctx) => {
    if (result === 'FAILED' && !comment?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['comment'],
        message: 'A comment is required when the result is FAILED',
      });
    }
  });

export const checkAppointmentSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ABSENT'),
  }),
  z.object({
    status: z.literal('COMPLETED'),
    devices: z.array(deviceCheckItemSchema).min(1),
  }),
]);

export type CheckAppointmentInput = z.infer<typeof checkAppointmentSchema>;
